<template>
  <div class="flex flex-col md:flex-row flex-nowrap md:flex-wrap gap-y-3">
    <Field name="poBoxFlag" v-slot="{ field, errors: errs, handleChange }">
      <RadioGroup
        :field="field"
        :errors="errs"
        :label="$t('havePoBoxValue')"
        :radio-options="[
          {
            label: $t('No'),
            value: 0
          },
          {
            label: $t('Yes'),
            value: 1
          }
        ]"
        class="px-1.5 w-full md:w-1/2"
        direction="flex-col"
        @on-change="
          (item) => {
            handleChange(item.value)
            resetField('postalBranchId')
            resetField('cityId')
            resetField('cityZoneId')
            resetField('emirateId')
            resetField('poBoxNumber')
          }
        "
      ></RadioGroup>
    </Field>
    <Field :rules="requiredRule" name="emirateId" v-slot="{ field, errors: errs, handleChange }">
      <Dropdown
        :field="field"
        :errors="errs"
        :label="$t('emirate')"
        :options="addressMgmtStore.emirates"
        class="px-0.5 w-full md:w-1/2"
        direction="flex-col"
        @on-selection="
          (item) => {
            if (!item) {
              resetField('emirateId')
            }
            resetField('cityId')
            resetField('cityZoneId')
            resetField('postalBranchId')
            handleChange(+item?.id)
          }
        "
      ></Dropdown>
    </Field>
    <Field
      :rules="values.poBoxFlag == 1 && requiredRule"
      name="poBoxNumber"
      v-slot="{ field, errors: errs }"
    >
      <TextInput
        :field="field"
        :errors="errs"
        :label="$t('poBoxNumber')"
        :is-disabled="values.poBoxFlag == 0"
        :is-required="values.poBoxFlag == 1"
        class="px-0.5 w-full md:w-1/2"
        direction="flex-col"
        @on-blur="fetchPostalBranch(values, setFieldValue)"
      />
    </Field>
    <Field
      :rules="values.poBoxFlag == 0 && requiredRule"
      name="postalBranchId"
      v-slot="{ field, errors: errs, handleChange }"
    >
      <Dropdown
        :key="`${values.emirateId}-${values.deliveryMethodId}-${values.deliveryCompanyId}`"
        :field="field"
        :errors="errs"
        :label="$t('postalBranch')"
        :options="addressMgmtStore.postalBranches.filter((p) => p.emirateId == values.emirateId)"
        :is-required="values.poBoxFlag == 0"
        :disabled="values.poBoxFlag == 1"
        class="px-0.5 w-full md:w-1/2"
        direction="flex-col"
        @on-selection="
          (item) => {
            handleChange(+item?.id)
            onPostalBranchChange(item, setFieldValue)
          }
        "
      ></Dropdown>
    </Field>
    <Field :rules="requiredRule" name="cityId" v-slot="{ field, errors: errs, handleChange }">
      <Dropdown
        :key="values.emirateId"
        :field="field"
        :errors="errs"
        :label="$t('city')"
        :options="addressMgmtStore.emirateCities[values.emirateId]?.cities"
        :disabled="values.deliveryMethodId == 1"
        class="px-0.5 w-full md:w-1/4"
        direction="flex-col"
        @on-selection="
          (item) => {
            if (!item) {
              resetField('cityId')
              resetField('cityZoneId')
              return
            }
            handleChange(+item?.id)
          }
        "
      ></Dropdown>
    </Field>
    <Field :rules="requiredRule" name="cityZoneId" v-slot="{ field, errors: errs, handleChange }">
      <Dropdown
        :key="`${values.emirateId}-${values.cityId}`"
        :field="field"
        :errors="errs"
        :label="$t('area')"
        :options="addressMgmtStore.cityZones.filter((cz) => cz.cityId == values.cityId)"
        :disabled="values.deliveryMethodId == 1"
        class="px-0.5 w-full md:w-1/4"
        direction="flex-col"
        @on-selection="(item) => handleChange(+item?.id)"
      ></Dropdown>
    </Field>
    <Field :rules="validEmail" name="email" v-slot="{ field, errors: errs }">
      <TextInput
        :field="field"
        :errors="errs"
        :label="$t('email')"
        class="px-0.5 w-full md:w-1/2"
        direction="flex-col"
      />
    </Field>
    <Field :rules="requiredRule" name="enAddressDetails" v-slot="{ field, errors: errs }">
      <TextInput
        :field="field"
        :errors="errs"
        :label="$t('addressEnglishDescription')"
        class="px-0.5 w-full md:w-1/2"
        direction="flex-col"
        @on-blur="translateField(field.value, setFieldValue, 'arAddressDetails')"
      />
    </Field>
    <Field :rules="requiredRule" name="arAddressDetails" v-slot="{ field, errors: errs }">
      <TextInput
        :field="field"
        :errors="errs"
        :label="$t('addressArabicDescription')"
        class="px-0.5 w-full md:w-1/2"
        direction="flex-col"
      />
    </Field>

    <Field :rules="requiredRule" name="mobileNumber" v-slot="{ field, errors: errs, handleChange }">
      <ComboText
        :field="field"
        :errors="errs"
        :label="$t('indivUaeMobileNumber')"
        :options="addressMgmtStore.countries"
        :is-prefix-disabled="true"
        input-class="px-3 py-1.5"
        class="px-0.5 w-full md:w-1/2"
        direction="flex-col"
        @on-selection="(phone) => handleChange(phone)"
      ></ComboText>
    </Field>
    <Field :rules="requiredRule" name="phoneNumber" v-slot="{ field, errors: errs, handleChange }">
      <ComboText
        :field="field"
        :errors="errs"
        :label="$t('fixedAbroadMobile')"
        :options="addressMgmtStore.countries"
        :is-prefix-disabled="true"
        input-class="px-3 py-1.5"
        class="px-0.5 w-full md:w-1/2"
        direction="flex-col"
        @on-selection="(phone) => handleChange(phone)"
      ></ComboText>
    </Field>
  </div>
</template>

<script setup>
import ComboText from '@/components/SimpleForm/ComboText.vue'
import Dropdown from '@/components/SimpleForm/Dropdown.vue'
import RadioGroup from '@/components/SimpleForm/RadioGroup.vue'
import TextInput from '@/components/SimpleForm/TextInput.vue'
import { isValidEmail } from '@/composables/useRuleHandler.js'
import axiosInstance from '@/services'
import { useIndAddressMgmtStore } from '@/stores/ind_address_mgmt.js'
import { Field } from 'vee-validate'
import { nextTick } from 'vue'
import { useI18n } from 'vue-i18n'

const i18n = useI18n()
const addressMgmtStore = useIndAddressMgmtStore()

function requiredRule(value) {
  if (!value || !`${value}`.trim()) {
    return i18n.t('INVALID_REQUIRED')
  }

  return true
}

function validEmail(value) {
  if (!value || !`${value}`.trim()) {
    return i18n.t('INVALID_REQUIRED')
  }

  return isValidEmail(value) || 'INVALID_EMAIL'
}

defineProps({
  values: Array,
  setFieldValue: Function,
  resetField: Function,
  isReadOnly: Boolean
})

async function translateField(source, setFieldValue, destination) {
  const response = await axiosInstance.get(`/api/translation/${source}`)
  if (!response) return
  setFieldValue(destination, JSON.parse(response)[source.toLowerCase()])
}

function onPostalBranchChange(postalBranch, setFieldValue) {
  const { cityId, cityZoneId } = postalBranch
  setFieldValue('cityId', cityId)
  setFieldValue('cityZoneId', cityZoneId)
}

async function fetchPostalBranch(form, setFieldValue) {
  const { emirateId, poBoxNumber } = form
  if ([emirateId, poBoxNumber].some((a) => !a)) return

  const body = {
    emirateId,
    poBoxNumber
  }
  const response = await axiosInstance.post('/api/lookup/getPostalBranchDetails', body)

  const { postalBranch } = response
  addressMgmtStore.postalBranches.push([
    {
      ...postalBranch,
      label: postalBranch.text || postalBranch.localizedName,
      value: postalBranch.id
    }
  ])
  nextTick()
  setFieldValue('postalBranchId', postalBranch.id)
  setFieldValue('cityId', postalBranch.cityId)
  setFieldValue('cityZoneId', postalBranch.cityZoneId)
}
</script>

<style lang="scss" scoped></style>
